import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Info, FileText, ChevronLeft, Package, Receipt, XCircle, DollarSign } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import TextArea from '../components/ui/TextArea';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchPurchaseOrdersForVendor, fetchCurrencies } from '../apiHelper';

const client = new FastAPIClient();

const VendorGoodsReceiptWithoutPayment = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const vendorId = location.state?.vendor_id;
    const vendorName = location.state?.vendor_name || 'Selected Vendor';
    const ponumber = location.state?.ponumber;

    const [user, setUser] = useState(null);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState('');
    const [loadingData, setLoadingData] = useState(true);
    const [dataErrors, setDataErrors] = useState(null);

    const [selectedPO, setSelectedPO] = useState(null);
    const [receivedItems, setReceivedItems] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to record goods receipts.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    useEffect(() => {
        const loadInitialData = async () => {
            if (!user || !vendorId) return;
            setLoadingData(true);
            try {
                const [fetchedPOs, fetchedCurrencies] = await Promise.all([
                    fetchPurchaseOrdersForVendor(vendorId),
                    fetchCurrencies(user)
                ]);

                setPurchaseOrders(fetchedPOs);
                const currencyOptions = fetchedCurrencies.map(curr => ({
                    label: `${curr.setccurr} (${curr.setcnote})`,
                    value: curr.setccurr,
                }));
                setCurrencies(currencyOptions);

                if (currencyOptions.length > 0) {
                    setSelectedCurrency(currencyOptions[0].value);
                }

                if (ponumber) {
                    const preselectedPO = fetchedPOs.find(po => po.ponumber === ponumber);
                    if (preselectedPO) setSelectedPO(preselectedPO);
                }
            } catch (err) {
                setDataErrors(err.response?.data?.detail || "Failed to load data.");
            } finally {
                setLoadingData(false);
            }
        };
        loadInitialData();
    }, [user, vendorId, ponumber]);

    useEffect(() => {
        if (selectedPO && selectedPO.poitems) {
            setReceivedItems(selectedPO.poitems.map(item => ({
                podetail_id: item.podetail_id,
                catentry_id: item.catentry_id,
                qtyordered: item.qtyordered,
                qtyreceived_current: item.qtyreceived,
                qtyremaining: item.qtyremaining,
                qtyreceived_input: '',
                listprice: item.catentry.listprice,
                // cost: '',
                // selling_price: '',
                comment: '', // Restored state
                productname: item.catentry?.productname || 'N/A',
                partnumber: item.catentry?.partnumber || 'N/A',
                store_id: selectedPO.store_id,
                vendor_id: selectedPO.vendor_id,
            })));
        }
    }, [selectedPO]);

    const handleReceivedItemChange = useCallback((podetailId, field, value) => {
        setReceivedItems(prev => prev.map(item => 
            item.podetail_id === podetailId ? { ...item, [field]: value } : item
        ));
    }, []);

    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;
        if (!selectedPO) { newErrors.poSelection = "Required"; isValid = false; }
        if (!selectedCurrency) { newErrors.currency = "Required"; isValid = false; }

        const itemsToReceive = receivedItems.filter(item => parseFloat(item.qtyreceived_input) > 0);
        if (itemsToReceive.length === 0) {
            newErrors.general = "Enter quantity for at least one item.";
            isValid = false;
        }

        setFormErrors(newErrors);
        return isValid;
    }, [selectedPO, selectedCurrency, receivedItems]);

    const handleSubmitReceipt = useCallback(async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            const itemsToSubmit = receivedItems
                .filter(item => parseFloat(item.qtyreceived_input) > 0)
                .map(item => ({
                    ...item,
                    qtyreceived: parseFloat(item.qtyreceived_input),
                    // cost: parseFloat(item.cost) || 0,
                    // selling_price: parseFloat(item.selling_price) || 0,
                    comment: item.comment.trim(), // Sending restored comment
                    receipttype: 'POGR',
                    recorder_id: user.users_id,
                }));
            
            const payload = {
                items: itemsToSubmit,
                purchaseorder_id: selectedPO.purchaseorder_id,
                setccurr: selectedCurrency,
                recorder_id: user.users_id
            }
            // console.log(payload);
            await client.postData('/vendor/receive-goods', payload);
            showToast("Receipt recorded successfully!", "success");
            setSelectedPO(null);
        } catch (err) {
            showToast("Error saving receipt.", "error");
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedPO, receivedItems, user, selectedCurrency, validateForm, showToast]);

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <Receipt size={24} className="text-green-600" /> Goods Receipt: {vendorName}
                    </h2>
                    <Button onClick={() => navigate(-1)} variant="secondary" size="sm">
                        <ChevronLeft size={20} className="mr-2" /> Back
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <SearchableSelectBox
                        label="Purchase Order"
                        required={true}
                        value={selectedPO?.purchaseorder_id || ''}
                        onChange={(e) => setSelectedPO(purchaseOrders.find(po => po.purchaseorder_id === parseInt(e.target.value)))}
                        options={purchaseOrders.map(po => ({ label: po.ponumber, value: po.purchaseorder_id }))}
                        icon={<FileText size={18} />}
                        error={formErrors.poSelection}
                    />
                    <SearchableSelectBox
                        label="Currency"
                        required
                        value={selectedCurrency}
                        onChange={(e) => setSelectedCurrency(e.target.value)}
                        options={currencies}
                        icon={<DollarSign size={18} />}
                        error={formErrors.currency}
                    />
                </div>

                {selectedPO && (
                    <div className="space-y-6">
                        {receivedItems.map((item) => (
                            <div key={item.podetail_id} className="p-4 border rounded-lg bg-gray-50">
                                <h4 className="font-semibold text-text-primary mb-2">{item.productname}</h4>
                                <p className="text-sm text-gray-600 mb-4">
                                    Remaining: {item.qtyremaining} 
                                    {/* | 
                                    <span className="text-blue-700 ml-1">Current Price: {item.listprice.listprice}</span> */}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <Input
                                        label="Qty Received"
                                        type="number"
                                        required
                                        value={item.qtyreceived_input}
                                        onChange={(e) => handleReceivedItemChange(item.podetail_id, 'qtyreceived_input', e.target.value)}
                                    />
                                    {/* <Input
                                        label="Cost"
                                        type="number"
                                        required
                                        value={item.cost}
                                        onChange={(e) => handleReceivedItemChange(item.podetail_id, 'cost', e.target.value)}
                                    />
                                    <Input
                                        label="New Price"
                                        type="number"
                                        value={item.selling_price}
                                        onChange={(e) => handleReceivedItemChange(item.podetail_id, 'selling_price', e.target.value)}
                                        placeholder={item.listprice.listprice}
                                    /> */}
                                    <div className="md:col-span-3">
                                        {/* RESTORED: Comments TextArea */}
                                        <TextArea
                                            label="Item Comments"
                                            placeholder="Add notes about this specific item shipment..."
                                            value={item.comment}
                                            onChange={(e) => handleReceivedItemChange(item.podetail_id, 'comment', e.target.value)}
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <div className="flex justify-end">
                            <Button type="submit" onClick={handleSubmitReceipt} variant="primary" size="lg" disabled={isSubmitting}>
                                {isSubmitting ? 'Recording...' : 'Record Goods Receipt'}
                            </Button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default VendorGoodsReceiptWithoutPayment;